import { mergeProps } from "@react-aria/utils";
import { useToastState } from "@react-stately/toast";
import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from "react";
import { useFocusWithin, useHover } from "react-aria";

import { ToastVariant } from "ds/components/ToastNew";
import useTypedContext from "hooks/useTypedContext";
import { FlashEvent } from "types/FlashMessage";
import { useReducedMotion } from "views/Theme/useReducedMotion";

import FlashContext from "./FlashContext";
import FlashMessagesRegion from "./Region";
import { getMessageTimeout } from "./utils";
import { wrapToastQueueUpdate } from "./wrapToastQueueUpdate";

type FlashMessagesProps = {
  defaultTimeout?: number;
};

const FlashMessages = ({ defaultTimeout }: FlashMessagesProps) => {
  const { subscribe, unsubscribe } = useTypedContext(FlashContext);
  const { reducedMotion } = useReducedMotion();
  const reducedMotionRef = useRef(reducedMotion);

  useLayoutEffect(() => {
    reducedMotionRef.current = reducedMotion;
  }, [reducedMotion]);

  const wrapUpdate = useCallback(
    (fn: () => void, action: Parameters<typeof wrapToastQueueUpdate>[1]) => {
      wrapToastQueueUpdate(fn, action, reducedMotionRef.current);
    },
    []
  );

  const state = useToastState<FlashEvent>({
    maxVisibleToasts: Infinity,
    wrapUpdate,
  });
  const visibleErrorToastIdRef = useRef<string | undefined>(undefined);

  const rerender = useReducer((s) => !s, false)[1];

  const { hoverProps } = useHover({
    onHoverStart: rerender,
    onHoverEnd: rerender,
  });

  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: rerender,
    onBlurWithin: rerender,
  });

  const addMessage = useCallback(
    (flashEvent: FlashEvent) => {
      const visibleToastId = visibleErrorToastIdRef.current;

      if (flashEvent.variant === ToastVariant.Error && visibleToastId) {
        state.close(visibleToastId);
      }

      const toastId = state.add(flashEvent, {
        timeout: getMessageTimeout(flashEvent, defaultTimeout),
        onClose: () => {
          if (flashEvent.variant === ToastVariant.Error) {
            visibleErrorToastIdRef.current = undefined;
          }
        },
      });

      if (flashEvent.variant === ToastVariant.Error) {
        visibleErrorToastIdRef.current = toastId;
      }
    },
    [defaultTimeout, state]
  );

  useEffect(
    function keepSubscribed() {
      subscribe(addMessage);
      return () => unsubscribe(addMessage);
    },
    [addMessage, subscribe, unsubscribe]
  );

  if (state.visibleToasts.length === 0) {
    return null;
  }

  return (
    <div tabIndex={-1} {...mergeProps(hoverProps, focusWithinProps)}>
      <FlashMessagesRegion state={state} />
    </div>
  );
};

export default FlashMessages;
